import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { CountBadge } from '@/components/admin/CountBadge';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import { ForbiddenState } from '@/components/admin/states';
import {
  ATTENDANCE_PART_LABELS,
  ATTENDANCE_SOURCE_LABELS,
  ATTENDANCE_STATUS_LABELS,
  attendanceExportUrl,
  fetchAbsenceRequests,
  fetchAttendance,
  manualUpdateAttendance,
  selfUpdateAttendance,
  type Attendance,
  type AttendancePart,
  type AttendanceStatus,
} from '@/features/events/attendanceApi';
import { EventAbsenceRequestsSection } from '@/features/events/EventAbsenceRequestsSection';
import type { ParticipationFormat } from '@/features/events/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

type SelfStatus = 'onsite' | 'online' | 'absent';

const SELF_STATUS_OPTIONS: { value: SelfStatus; label: string }[] = [
  { value: 'onsite', label: '現地参加' },
  { value: 'online', label: 'オンライン参加' },
  { value: 'absent', label: '欠席' },
];

/** 出席として数える状態。遅刻はMeet参加ログの取込で自動的に付く（D-117） */
const ATTENDING_STATUSES: AttendanceStatus[] = ['onsite', 'online', 'late'];

function selfStatusOptions(format: ParticipationFormat): { value: SelfStatus; label: string }[] {
  if (format === 'offline') return SELF_STATUS_OPTIONS.filter((o) => o.value !== 'online');
  if (format === 'online') return SELF_STATUS_OPTIONS.filter((o) => o.value !== 'onsite');
  return SELF_STATUS_OPTIONS;
}

/**
 * 受講生に見せる区分の見出し。「本編」だけでは何を指すのか分からない
 * という実機フィードバックを受けて、説明を添える（D-179）。
 */
const PART_HEADINGS: Record<AttendancePart, string> = {
  main: '本編（イベント当日のプログラム）',
  social: '交流会（本編のあとの懇親会）',
};

/** 出欠タブ（本編/交流会別 / 指示書 §4・§6）. */
export function EventAttendanceTab({
  eventId,
  hasSocial,
  isPublished,
  attendanceRequired,
  attendanceDeadlineAt,
  participationFormat,
  typeCode,
  capacity,
  capacityFull,
  attendingCount,
}: {
  eventId: string;
  hasSocial: boolean;
  isPublished: boolean;
  attendanceRequired: boolean;
  attendanceDeadlineAt: string | null;
  participationFormat: ParticipationFormat;
  typeCode?: string | undefined;
  capacity?: number | null;
  capacityFull?: boolean;
  attendingCount?: number | null;
}): ReactElement {
  const { user } = useAuth();
  const isStudent = user?.active_role === 'student';
  // 出欠の変更・CSV出力は事務局のみ（D-309）。ヒアリングシート①の運営フローでも
  // 出欠の確定は事務局の作業で、理事・講師／サクセス担当は出席者を見るだけ
  const canManage = user?.active_role === 'office';
  const canViewList =
    canManage || user?.active_role === 'director' || user?.active_role === 'success';

  if (!isStudent && !canViewList) {
    return <ForbiddenState />;
  }

  // 出欠を取らない回は申請フォームを出さない。行が無いのを「対象外」と誤認させない（D-227）
  if (!attendanceRequired) {
    return (
      <div>
        <h3 className="mb-1 font-semibold text-wptext-strong">出欠</h3>
        <p className="text-wptext-muted">
          このイベントは出欠を取りません。参加・欠席の申請は不要で、出欠の集計対象にもなりません。
        </p>
      </div>
    );
  }

  if (!isPublished) {
    return (
      <div>
        <p className="text-wptext-muted">公開すると出欠の受付が始まります。</p>
        {attendanceDeadlineAt && (
          <p className="mt-1 text-wptext-muted">
            回答期限: {formatDateTime(attendanceDeadlineAt)}
          </p>
        )}
      </div>
    );
  }

  return (
    <PublishedAttendance
      eventId={eventId}
      hasSocial={hasSocial}
      participationFormat={participationFormat}
      attendanceDeadlineAt={attendanceDeadlineAt}
      typeCode={typeCode}
      capacity={capacity ?? null}
      capacityFull={capacityFull === true}
      attendingCount={attendingCount ?? null}
    />
  );
}

function PublishedAttendance({
  eventId,
  hasSocial,
  participationFormat,
  attendanceDeadlineAt,
  typeCode,
  capacity,
  capacityFull,
  attendingCount,
}: {
  eventId: string;
  hasSocial: boolean;
  participationFormat: ParticipationFormat;
  attendanceDeadlineAt: string | null;
  typeCode?: string | undefined;
  capacity: number | null;
  capacityFull: boolean;
  attendingCount: number | null;
}): ReactElement {
  const { user } = useAuth();
  const isStudent = user?.active_role === 'student';
  const canManage = user?.active_role === 'office';
  const canViewList =
    canManage || user?.active_role === 'director' || user?.active_role === 'success';
  // 出欠の行はイベント公開時に対象者ぶんだけ作られる（D-115）。受講生に行が無ければ対象外で、
  // 出欠の回答も欠席申請もAPI側で弾かれる。押しても必ず失敗する操作は出さない（D-152）
  const query = useQuery({
    queryKey: ['attendance', eventId],
    queryFn: () => fetchAttendance(eventId),
  });
  const isTarget = !isStudent || (query.data?.length ?? 0) > 0;

  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;

  return (
    <div className="flex flex-col gap-6">
      {attendanceDeadlineAt && (
        <p className="text-wptext-muted">回答期限: {formatDateTime(attendanceDeadlineAt)}</p>
      )}
      {capacity !== null && attendingCount !== null && (
        <p className="text-wptext-muted">
          定員 {capacity}名（確定 {attendingCount}名）
          {capacityFull ? ' — 定員に達しています' : ''}
        </p>
      )}
      {isStudent && (
        <SelfAttendance
          eventId={eventId}
          hasSocial={hasSocial}
          participationFormat={participationFormat}
          attendanceDeadlineAt={attendanceDeadlineAt}
          typeCode={typeCode}
          capacity={capacity}
          capacityFull={capacityFull}
        />
      )}
      {canManage ? (
        <AttendanceManagement
          eventId={eventId}
          participationFormat={participationFormat}
          capacity={capacity}
          attendingCount={attendingCount}
          capacityFull={capacityFull}
        />
      ) : (
        canViewList && (
          <AttendeeList
            eventId={eventId}
            participationFormat={participationFormat}
            capacity={capacity}
            attendingCount={attendingCount}
            capacityFull={capacityFull}
          />
        )
      )}
      {/* 欠席申請は受講生が出し、事務局が承認する。理事・講師／サクセス担当には出さない（D-309） */}
      {(isStudent || canManage) && (
        <EventAbsenceRequestsSection eventId={eventId} canRequest={isStudent && isTarget} />
      )}
    </div>
  );
}

/**
 * 受講生本人の出欠回答（D-179）.
 *
 * 以前は「ボタンを押した時点で即登録」だったが、選び間違いがそのまま反映されて
 * しまい取り消せる実感も無かった（実機フィードバック）。**ラジオで選んでから
 * 「この内容で申請する」を押す**流れに変えた。申請後も選び直して再申請できる。
 */
function SelfAttendance({
  eventId,
  hasSocial,
  participationFormat,
  attendanceDeadlineAt,
  typeCode,
  capacity,
  capacityFull,
}: {
  eventId: string;
  hasSocial: boolean;
  participationFormat: ParticipationFormat;
  attendanceDeadlineAt: string | null;
  typeCode?: string | undefined;
  capacity: number | null;
  capacityFull: boolean;
}): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['attendance', eventId], queryFn: () => fetchAttendance(eventId) });
  const absences = useQuery({
    queryKey: ['absence-requests', eventId],
    queryFn: () => fetchAbsenceRequests(eventId),
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 未申請のあいだの選択（part ごと）。null は「まだ触っていない」
  const [draft, setDraft] = useState<Partial<Record<AttendancePart, SelfStatus>>>({});

  if (query.isPending || absences.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;

  const parts: AttendancePart[] = hasSocial ? ['main', 'social'] : ['main'];
  const own = query.data;
  const options = selfStatusOptions(participationFormat);
  const absenceLocked = (absences.data ?? []).some(
    (r) => r.status === 'pending' || r.status === 'approved',
  );
  const deadlinePassed =
    attendanceDeadlineAt !== null && new Date(attendanceDeadlineAt).getTime() < Date.now();
  const locked = absenceLocked || deadlinePassed;
  const isSubcommitteeFcfs = typeCode === 'subcommittee' && capacity !== null;
  const mainRow = own.find((r) => r.part === 'main');
  const alreadyAttending =
    mainRow !== undefined &&
    mainRow.status !== 'unanswered' &&
    ATTENDING_STATUSES.includes(mainRow.status);
  const capacityBlocked =
    isSubcommitteeFcfs && capacityFull && !alreadyAttending && !mainRow?.waitlisted;

  // 出欠の行はイベント公開時に対象者ぶんだけ作られる（D-115）。行が無い＝対象外なので、
  // 押しても必ず失敗するボタンを出さない（API は 404 を返す / D-152）
  if (own.length === 0) {
    return (
      <div>
        <h3 className="mb-1 font-semibold text-wptext-strong">あなたの出欠</h3>
        <p className="text-wptext-muted">
          あなたはこの回の対象者ではないため、出欠の回答はありません。
          対象に入るはずの場合は事務局にお問い合わせください。
        </p>
      </div>
    );
  }

  /** 画面上の選択（未操作なら登録済みの値） */
  function chosen(part: AttendancePart): SelfStatus | null {
    if (draft[part]) return draft[part] ?? null;
    const row = own.find((r) => r.part === part);
    const current = row?.status;
    return current && current !== 'unanswered' ? (current as SelfStatus) : null;
  }

  /** 登録済みの内容から変わっているか（申請ボタンの出し分けに使う） */
  const changed = parts.some((part) => {
    const row = own.find((r) => r.part === part);
    return draft[part] !== undefined && draft[part] !== row?.status;
  });
  const allChosen = parts.every((part) => chosen(part) !== null);

  async function onSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const done: string[] = [];
      for (const part of parts) {
        const status = chosen(part);
        const row = own.find((r) => r.part === part);
        if (status === null || status === row?.status) continue;
        await selfUpdateAttendance(eventId, part, status);
        const label = options.find((o) => o.value === status)?.label ?? '';
        done.push(hasSocial ? `${ATTENDANCE_PART_LABELS[part]}: ${label}` : label);
      }
      await queryClient.invalidateQueries({ queryKey: ['attendance', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setDraft({});
      setSaved(
        done.length > 0
          ? `出欠を申請しました（${done.join(' / ')}）。あとから選び直して再申請できます。`
          : '内容に変更はありませんでした。',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '申請に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h3 className="mb-1 font-semibold text-wptext-strong">あなたの出欠</h3>
      {absenceLocked ? (
        <p className="mb-2 text-wptext-muted">
          欠席申請のあと出欠は変更できません。差し戻された場合のみ選び直せます。
        </p>
      ) : deadlinePassed ? (
        <p className="mb-2 text-wptext-muted">
          出欠の回答期限（{formatDateTime(attendanceDeadlineAt)}）を過ぎているため、変更できません。
          変更が必要な場合は事務局にお問い合わせください。
        </p>
      ) : capacityBlocked ? (
        <p className="mb-2 text-wptext-muted">
          定員（{capacity}名）に達しているため、参加の申請はできません。欠席のみ選べます。
        </p>
      ) : (
        <p className="mb-2 text-wptext-muted">
          参加のしかたを選んで、<strong>「この内容で申請する」</strong>を押してください。
          押すまでは登録されません。<strong>申請したあとでも選び直して再申請できます。</strong>
        </p>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {saved && (
        <Notice kind="success" onDismiss={() => setSaved(null)}>
          {saved}
        </Notice>
      )}
      <div className="flex flex-col gap-3">
        {parts.map((part) => {
          const row = own.find((r) => r.part === part);
          const selected = chosen(part);
          return (
            <div key={part} className="flex flex-wrap items-center gap-3">
              {/* 交流会がない回で「本編」とだけ書かれても何のことか分からない（D-179）。
                  分かれているときだけ、説明つきの見出しを出す */}
              {hasSocial && (
                <span className="w-44 text-wptext-muted">{PART_HEADINGS[part]}</span>
              )}
              {options.map((opt) => {
                const attendBlocked =
                  capacityBlocked && part === 'main' && opt.value !== 'absent';
                return (
                <label key={opt.value} className="flex cursor-pointer items-center gap-1">
                  <input
                    type="radio"
                    name={`attendance-${part}`}
                    checked={selected === opt.value}
                    disabled={busy || locked || attendBlocked}
                    onChange={() => setDraft((prev) => ({ ...prev, [part]: opt.value }))}
                  />
                  {opt.label}
                </label>
              );
              })}
              <span className="text-wptext-muted">
                {row?.waitlisted
                  ? '（現在: キャンセル待ち）'
                  : row && row.status !== 'unanswered'
                    ? `（申請済み: ${ATTENDANCE_STATUS_LABELS[row.status]}）`
                    : '（未申請）'}
              </span>
            </div>
          );
        })}
      </div>
      {!locked && (
      <div className="mt-3">
        <button
          type="button"
          disabled={busy || !allChosen || !changed}
          onClick={() => void onSubmit()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          {busy ? '申請中…' : 'この内容で申請する'}
        </button>
        {!changed && allChosen && (
          <span className="ml-2 text-wptext-muted">
            申請済みの内容と同じです。変更したいときは選び直してください。
          </span>
        )}
      </div>
      )}
      <p className="mt-2 text-xs text-wptext-muted">
        「欠席」で申請すれば欠席の連絡は完了です。事務局に理由を伝えて記録として残したい場合だけ、
        下の「欠席申請」を使ってください。
      </p>
    </div>
  );
}


/**
 * 理事・講師／サクセス担当に見せる出席者一覧（D-309）。
 *
 * 出欠の確定は事務局の作業なので、変更・CSV出力・欠席申請・「状態」「出所」列は出さず、
 * **出席する人の氏名だけ**を並べる。来ない人を黙って消したことにしないよう、
 * 未回答・欠席・キャンセル待ちは人数だけ末尾に添える。
 */
function AttendeeList({
  eventId,
  participationFormat,
  capacity,
  attendingCount,
  capacityFull,
}: {
  eventId: string;
  participationFormat: ParticipationFormat;
  capacity?: number | null;
  attendingCount?: number | null;
  capacityFull?: boolean;
}): ReactElement {
  const query = useQuery({
    queryKey: ['attendance', eventId],
    queryFn: () => fetchAttendance(eventId),
  });

  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;

  // 出席扱いは現地・オンライン・遅刻。キャンセル待ちは定員超過でまだ確定していない
  const attending = query.data.filter(
    (row) => !row.waitlisted && ATTENDING_STATUSES.includes(row.status),
  );
  const others = query.data.length - attending.length;

  return (
    <div>
      <h3 className="mb-2 font-semibold text-wptext-strong">出席者一覧</h3>
      {capacity !== null && capacity !== undefined && attendingCount !== null && attendingCount !== undefined && (
        <p className="mb-2 text-sm text-wptext-muted">
          定員 {capacity}名（確定 {attendingCount}名）
          {capacityFull ? ' — 定員に達しています' : ''}
        </p>
      )}
      {attending.length === 0 ? (
        <p className="text-wptext-muted">出席の回答はまだありません。</p>
      ) : (
        <>
          <p className="mb-2 text-xs text-wptext-muted">
            氏名を押すと、その受講生のプロフィール（公開項目）を開けます。
          </p>
          <ul className="flex flex-col gap-1">
            {attending.map((row) => (
              <li key={row.id}>
                <Link
                  to={`/members/${row.member_id}`}
                  className="text-wpaccent hover:underline"
                >
                  {row.member_name ?? row.member_id}
                </Link>
                {/* 現地とオンラインが混ざる回だけは、どちらで参加するかを添える */}
                {participationFormat === 'both' && (
                  <span className="ml-2 text-xs text-wptext-muted">
                    {ATTENDANCE_STATUS_LABELS[row.status]}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {others > 0 && (
        <p className="mt-2 text-xs text-wptext-muted">
          このほかに未回答・欠席・キャンセル待ちが{others}名います（内訳は事務局が管理します）。
        </p>
      )}
    </div>
  );
}

/** 事務局用の出欠管理（変更・CSV出力つき）。ほかのロールは AttendeeList を見る（D-309） */
function AttendanceManagement({
  eventId,
  participationFormat,
  capacity,
  attendingCount,
  capacityFull,
}: {
  eventId: string;
  participationFormat: ParticipationFormat;
  capacity?: number | null;
  attendingCount?: number | null;
  capacityFull?: boolean;
}): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['attendance', eventId], queryFn: () => fetchAttendance(eventId) });
  const absences = useQuery({
    queryKey: ['absence-requests', eventId],
    queryFn: () => fetchAbsenceRequests(eventId),
  });
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<Attendance | null>(null);
  const [busy, setBusy] = useState(false);

  async function onDecide(status: 'onsite' | 'online' | 'absent'): Promise<void> {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await manualUpdateAttendance(eventId, target.member_id, target.part, status);
      await queryClient.invalidateQueries({ queryKey: ['attendance', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setTarget(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;

  const pendingByMember = new Map<string, number>();
  for (const request of absences.data ?? []) {
    if (request.status !== 'pending') continue;
    pendingByMember.set(request.member_id, (pendingByMember.get(request.member_id) ?? 0) + 1);
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-wptext-strong">出欠一覧</h3>
        <a
          href={attendanceExportUrl(eventId)}
          className="text-wpaccent hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          出欠一覧をCSV出力
        </a>
      </div>
      {capacity != null && attendingCount != null && (
        <p className="mb-2 text-sm text-wptext-muted">
          定員 {capacity}名（確定 {attendingCount}名）
          {capacityFull ? ' — 定員に達しています' : ''}
        </p>
      )}
      <p className="mb-2 text-xs text-wptext-muted">
        手元で席次表を作るときの元データとして使えます。卓の割付はシステムには取り込みません。
      </p>
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-wpborder bg-wpcanvas/60">
              <th className="px-2 py-1.5">氏名</th>
              <th className="px-2 py-1.5">区分</th>
              <th className="px-2 py-1.5">状態</th>
              <th className="px-2 py-1.5">出所</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {query.data.map((row) => (
              <tr key={row.id} className="border-b border-wpborder">
                <td className="px-2 py-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    <Link
                      to={`/members/${row.member_id}`}
                      className="text-wpaccent hover:underline"
                    >
                      {row.member_name ?? row.member_id}
                    </Link>
                    <CountBadge
                      count={pendingByMember.get(row.member_id) ?? 0}
                      label="欠席申請"
                    />
                  </span>
                </td>
                <td className="px-2 py-1.5">{ATTENDANCE_PART_LABELS[row.part]}</td>
                <td className="px-2 py-1.5">
                  {row.waitlisted ? 'キャンセル待ち' : ATTENDANCE_STATUS_LABELS[row.status]}
                </td>
                <td className="px-2 py-1.5">{ATTENDANCE_SOURCE_LABELS[row.source]}</td>
                <td className="px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => setTarget(row)}
                    className="text-wpaccent hover:underline"
                  >
                    変更
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 一覧の下に操作行を出すと、人数が多いとき画面外になって気づけない（D-094） */}
      <Modal
        open={target !== null}
        title={
          target
            ? `出欠の変更: ${target.member_name ?? target.member_id}（${ATTENDANCE_PART_LABELS[target.part]}）`
            : ''
        }
        onClose={() => setTarget(null)}
        footer={
          <button
            type="button"
            onClick={() => setTarget(null)}
            className="btn-secondary"
          >
            キャンセル
          </button>
        }
      >
        <p className="mb-3 text-wptext-muted">
          変更後の出欠を選んでください。押した時点で保存されます。
          事務局が変更したことは記録に残ります。
        </p>
        <div className="flex flex-wrap gap-2">
          {selfStatusOptions(participationFormat).map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={busy}
              onClick={() => void onDecide(option.value)}
              className={`rounded-sm border px-3.5 py-2 disabled:opacity-50 ${
                target?.status === option.value
                  ? 'border-wpaccent bg-wpaccent font-semibold text-white'
                  : 'btn-secondary'
              }`}
            >
              {target?.status === option.value ? `✓ ${option.label}` : option.label}
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
}
